/**
 * Clinical Study Swiper
 * Handles slide navigation with custom progress bar and number countdown animation
 */

// Number countdown animation function (enhanced: supports unit <span> and suffixes)
const animateNumber = (element, targetString, duration = 2500) => {
  const start = 0;
  const startTime = performance.now();

  element.classList.add('animating');

  const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

  // Extract numeric target and suffix (e.g. "92%" => 92 and "%")
  const numericMatch = String(targetString).match(/^(\d+)/);
  const numericTarget = numericMatch ? parseInt(numericMatch[1], 10) : 0;
  const suffix = numericMatch ? String(targetString).replace(/^\d+/, '') : '';

  const unitEl = element.querySelector('.data-banner__unit') || element.querySelector('.clinical-unit');

  // Find (or create) a text node before the unit element to update the numeric part
  let textNode = null;
  if (unitEl) {
    for (const node of element.childNodes) {
      if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
        textNode = node;
        break;
      }
    }
    if (!textNode) {
      textNode = document.createTextNode('0');
      element.insertBefore(textNode, unitEl);
    } else {
      textNode.textContent = '0';
    }
  } else {
    // No unit span; set full text including suffix
    element.textContent = '0' + suffix;
  }

  const updateNumber = (currentTime) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    const easedProgress = easeOutExpo(progress);
    const current = start + (numericTarget - start) * easedProgress;
    const currentNum = Math.round(current);

    if (unitEl && textNode) {
      textNode.textContent = currentNum;
    } else {
      element.textContent = currentNum + suffix;
    }

    if (progress < 1) {
      requestAnimationFrame(updateNumber);
    } else {
      // Ensure final exact value
      if (unitEl && textNode) {
        textNode.textContent = String(numericTarget);
      } else {
        element.textContent = String(numericTarget) + suffix;
      }

      setTimeout(() => {
        element.classList.add('complete');
        element.classList.remove('animating');
      }, 100);
    }
  };

  requestAnimationFrame(updateNumber);
};

// Initialize statistics animation (uses IntersectionObserver)
const initStatisticsAnimation = () => {
  const featuresSections = document.querySelectorAll('.clinical-study-wrapper');

  featuresSections.forEach(section => {
    const statsSection = section.querySelector('.clinical-stats-section');
    if (!statsSection) return;

    const statItems = statsSection.querySelectorAll('.clinical-stat-item');
    const statValues = statsSection.querySelectorAll('.clinical-stat-value');

    if (statValues.length === 0) return;

    let animated = false;
    const targetStrings = [];

    // Capture target strings and set initial 0 display
    statValues.forEach((valueEl, index) => {
      const target = valueEl.getAttribute('data-stat-target') || valueEl.textContent.trim();
      if (target) {
        // If there's an inline unit span in clinical, preserve it (look for .clinical-unit)
        const unitEl = valueEl.querySelector('.data-banner__unit') || valueEl.querySelector('.clinical-unit');
        if (unitEl) {
          targetStrings[index] = target.match(/^(\d+)/) ? target.match(/^(\d+)/)[0] : target;
          // Ensure a text node exists before unit
          let textNode = null;
          for (const node of valueEl.childNodes) {
            if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
              textNode = node;
              break;
            }
          }
          if (!textNode) valueEl.insertBefore(document.createTextNode('0'), unitEl);
          else textNode.textContent = '0';
        } else {
          targetStrings[index] = target;
          const suffix = String(target).replace(/^\d+/, '');
          valueEl.textContent = '0' + suffix;
        }
      }
    });

    const observerCallback = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !animated) {
          animated = true;

          statItems.forEach((item, index) => {
            setTimeout(() => {
              item.classList.add('animate-in');
            }, index * 200);
          });

          statValues.forEach((valueEl, index) => {
            if (targetStrings[index]) {
              setTimeout(() => {
                animateNumber(valueEl, targetStrings[index], 2500);
              }, index * 200 + 300);
            }
          });

          observer.unobserve(statsSection);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.2,
      rootMargin: '0px 0px -20% 0px',
    });

    observer.observe(statsSection);
  });
};

document.addEventListener('DOMContentLoaded', () => {

  // Initialize statistics animation
  initStatisticsAnimation();

  const swiperContainers = document.querySelectorAll('.clinical-swiper');

  swiperContainers.forEach(container => {    
    const paginationEl = window.innerWidth >= 1025 ? document.querySelector('.clinical-navigation--desktop .clinical-progress-dots') : document.querySelector('.clinical-navigation--mobile .clinical-progress-dots');
    const nextButtonEl = window.innerWidth >= 1025 ? document.querySelector('.clinical-navigation--desktop .clinical-nav-next') : document.querySelector('.clinical-navigation--mobile .clinical-nav-next');
    const previousButtonEl = window.innerWidth >= 1025 ? document.querySelector('.clinical-navigation--desktop .clinical-nav-prev') : document.querySelector('.clinical-navigation--mobile .clinical-nav-prev'); 
    const swiper = new Swiper(container, {
      slidesPerView: 1.2,
      spaceBetween: 21,
      loop: false,
      autoplay: false,
      grabCursor: true,
      slidesOffsetAfter: 16,
      pagination: {
        el: paginationEl,
        clickable: true,
        bulletClass: 'clinical-progress-dot',
        bulletActiveClass: 'is-active',
        renderBullet: function (index, className) {
          return `<span class="${className}" data-dot-index="${index}" aria-label="Go to slide ${index + 1}"></span>`;
        }
      },
      navigation: {
        nextEl: nextButtonEl,
        prevEl: previousButtonEl
      },
      breakpoints: {
        751: {
          slidesPerView: 2.2,
          spaceBetween: 21,
          slidesOffsetAfter: 16
        },
        1025: {
          slidesPerView: 1.2,
          spaceBetween: 24,
          slidesOffsetAfter: 24
        },
        1201: {
          slidesPerView: 2.2,
          slidesOffsetAfter: 24
        },
        1440: {
          slidesPerView: 2.2,
          slidesOffsetAfter: 24
        },
        1550: {
          slidesPerView: 2.0,
          slidesOffsetAfter: 24
        }
      }      
    });
  });
});

// Reinitialize on Shopify section load
document.addEventListener("shopify:section:load", function (event) {
  if (event.target.classList.contains('section-clinical-study')) {
    initStatisticsAnimation();
  }
});
